import React, { useMemo, useState } from 'react';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import Breadcrumb from '../components/Common/Breadcrumb';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { newSelect as Select } from '../components/ui/newSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const DEFAULT_WORKING_DAYS = 253;
const MAX_DAYS_IN_YEAR = 366;
const DEFAULT_OFFICE_TYPE_ID = 'air-conditioned-gas-heating-typical';
const CUSTOM_OFFICE_TYPE_ID = 'custom';

const DEFAULT_OFFICE_TYPES = [
  { id: 'air-conditioned-gas-heating-typical', name: 'Air Conditioned, Gas Heating, Typical', electricity: 119, gas: 59, water: 6 },
  { id: 'air-conditioned-gas-heating-good-practice', name: 'Air Conditioned, Gas Heating, Good Practice', electricity: 85, gas: 29, water: 4 },
  { id: 'air-conditioned-all-electric-typical', name: 'Air Conditioned, All-Electric, Typical', electricity: 107, gas: 0, water: 6 },
  { id: 'air-conditioned-all-electric-good-practice', name: 'Air Conditioned, All-Electric, Good Practice', electricity: 82, gas: 0, water: 4 },
  { id: 'naturally-ventilated-mixed-mode-gas-heating-typical', name: 'Naturally Ventilated / Mixed Mode, Gas Heating, Typical', electricity: 82, gas: 41, water: 6 },
  { id: 'naturally-ventilated-mixed-mode-gas-heating-good-practice', name: 'Naturally Ventilated / Mixed Mode, Gas Heating, Good Practice', electricity: 57, gas: 22, water: 4 },
  { id: 'naturally-ventilated-mixed-mode-all-electric-typical', name: 'Naturally Ventilated / Mixed Mode, All-Electric, Typical', electricity: 98, gas: 0, water: 6 },
  { id: 'naturally-ventilated-mixed-mode-all-electric-good-practice', name: 'Naturally Ventilated / Mixed Mode, All-Electric, Good Practice', electricity: 67, gas: 0, water: 4 },
];

const EMPTY_CUSTOM_FACTORS = { electricity: '', gas: '', water: '' };

const OFFICE_TYPE_OPTIONS = [
  { value: CUSTOM_OFFICE_TYPE_ID, label: 'Custom' },
  ...DEFAULT_OFFICE_TYPES.map((officeType) => ({ value: officeType.id, label: officeType.name })),
];

const breadcrumbItems = [{ label: 'Communal Office Spaces', link: '/communal-office-spaces' }];

const WORKING_DAYS_LIMIT_MESSAGE =
  'Enter 366 or fewer. Compare Your Footprint accepts usage for one year at a time, including leap years. Daily rates and totals are not calculated above this limit.';

const DAYS_OCCUPIED_LIMIT_MESSAGE =
  'Enter 366 or fewer. Compare Your Footprint accepts usage for one year at a time, including leap years. Totals are not calculated above this limit.';

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const exceedsYearLimit = (value) => {
  const parsed = parseNumber(value);
  return parsed !== null && parsed > MAX_DAYS_IN_YEAR;
};

/**
 * Converts an annual benchmark factor into a daily rate using total working days.
 * Mirrors LOOKUP(...)/workingDays from the Data Helper Tool.
 * @param {number|null} annualValue
 * @param {number|null} workingDays
 * @returns {number|null}
 */
const dailyRate = (annualValue, workingDays) => {
  if (annualValue === null || workingDays === null || workingDays === 0) return null;
  return annualValue / workingDays;
};

const formatNumber = (value, fractionDigits = 6) => {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
};

const CommunalOfficeSpaces = () => {
  const [workingDays, setWorkingDays] = useState(DEFAULT_WORKING_DAYS);
  const [customFactors, setCustomFactors] = useState(EMPTY_CUSTOM_FACTORS);
  const [selectedOfficeTypeId, setSelectedOfficeTypeId] = useState(DEFAULT_OFFICE_TYPE_ID);
  const [floorArea, setFloorArea] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [daysOccupied, setDaysOccupied] = useState('');

  const usingCustom = selectedOfficeTypeId === CUSTOM_OFFICE_TYPE_ID;

  const selectedFactors = useMemo(() => {
    if (usingCustom) return customFactors;
    return DEFAULT_OFFICE_TYPES.find((officeType) => officeType.id === selectedOfficeTypeId) || DEFAULT_OFFICE_TYPES[0];
  }, [customFactors, selectedOfficeTypeId, usingCustom]);

  const workingDaysOverLimit = exceedsYearLimit(workingDays);
  const daysOccupiedOverLimit = exceedsYearLimit(daysOccupied);
  const workingDaysValue = workingDaysOverLimit ? null : parseNumber(workingDays);
  const daysOccupiedValue = daysOccupiedOverLimit ? null : parseNumber(daysOccupied);

  const electricityPerM2PerDay = dailyRate(parseNumber(selectedFactors?.electricity), workingDaysValue);
  const gasPerM2PerDay = dailyRate(parseNumber(selectedFactors?.gas), workingDaysValue);
  const waterPerPersonPerDay = dailyRate(parseNumber(selectedFactors?.water), workingDaysValue);

  const floorAreaValue = parseNumber(floorArea);
  const employeeCountValue = parseNumber(employeeCount);
  const totalsBlocked = workingDaysOverLimit || daysOccupiedOverLimit;

  const electricityTotal =
    !totalsBlocked && electricityPerM2PerDay !== null && floorAreaValue !== null && daysOccupiedValue !== null
      ? electricityPerM2PerDay * floorAreaValue * daysOccupiedValue
      : null;
  const gasTotal =
    !totalsBlocked && gasPerM2PerDay !== null && floorAreaValue !== null && daysOccupiedValue !== null
      ? gasPerM2PerDay * floorAreaValue * daysOccupiedValue
      : null;
  const waterTotal =
    !totalsBlocked && waterPerPersonPerDay !== null && employeeCountValue !== null && daysOccupiedValue !== null
      ? waterPerPersonPerDay * employeeCountValue * daysOccupiedValue
      : null;

  const updateCustomFactor = (field, value) => {
    setCustomFactors((current) => ({ ...current, [field]: value }));
  };

  const resetCalculator = () => {
    setWorkingDays(DEFAULT_WORKING_DAYS);
    setCustomFactors(EMPTY_CUSTOM_FACTORS);
    setSelectedOfficeTypeId(DEFAULT_OFFICE_TYPE_ID);
    setFloorArea('');
    setEmployeeCount('');
    setDaysOccupied('');
  };

  return (
    <React.Fragment>
      <Breadcrumb items={breadcrumbItems} />
      <Card className='border rounded-md hover:translate-y-0'>
        <CardHeader className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle className='text-xl font-semibold'>Communal Office Spaces</CardTitle>
            <p className='text-sm text-muted-foreground pt-2 max-w-4xl'>
              This page helps you calculate your organisation's office emissions if you are in a communal office space.
            </p>
          </div>
          <Button variant='outline' size='sm' onClick={resetCalculator}>
            <RotateCcw size={16} strokeWidth={1.5} />
            Reset defaults
          </Button>
        </CardHeader>
        <CardContent className='space-y-8'>
          <div className='max-w-xl space-y-3'>
            <div className='max-w-xs'>
              <Label htmlFor='total-working-days'>Total working days in the year</Label>
              <Input
                id='total-working-days'
                type='number'
                min='1'
                max={MAX_DAYS_IN_YEAR}
                className='mt-2'
                value={workingDays}
                error={workingDaysOverLimit}
                aria-invalid={workingDaysOverLimit}
                aria-describedby={
                  workingDaysOverLimit ? 'total-working-days-help total-working-days-error' : 'total-working-days-help'
                }
                onChange={(e) => setWorkingDays(e.target.value)}
              />
              <p id='total-working-days-help' className='text-xs text-muted-foreground mt-2'>
                Used to convert annual electricity, gas, and water factors into daily rates for one year. Maximum 366
                days.
              </p>
            </div>
            {workingDaysOverLimit && (
              <Alert variant='warning' id='total-working-days-error'>
                <TriangleAlert size={16} strokeWidth={1.5} />
                <AlertDescription>{WORKING_DAYS_LIMIT_MESSAGE}</AlertDescription>
              </Alert>
            )}
          </div>

          <section className='space-y-3'>
            <h3 className='text-base font-semibold text-foreground'>Benchmark factors</h3>
            <p className='text-sm text-muted-foreground'>
              These pre-filled benchmark values are used in the daily rate formulas. The benchmark rows are view only.
              Enter your own figures in the Custom row (copy any benchmark values you want to keep), then choose Custom
              as your office type.
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Office type</TableHead>
                  <TableHead>Electricity (kWh/m²/year)</TableHead>
                  <TableHead>Gas (kWh/m²/year)</TableHead>
                  <TableHead>Water (m³/person/year)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DEFAULT_OFFICE_TYPES.map((officeType) => (
                  <TableRow
                    key={officeType.id}
                    className={officeType.id === selectedOfficeTypeId ? 'bg-muted/50' : undefined}>
                    <TableCell className='text-foreground'>{officeType.name}</TableCell>
                    <TableCell className='text-foreground tabular-nums'>{officeType.electricity}</TableCell>
                    <TableCell className='text-foreground tabular-nums'>{officeType.gas}</TableCell>
                    <TableCell className='text-foreground tabular-nums'>{officeType.water}</TableCell>
                  </TableRow>
                ))}
                <TableRow className={usingCustom ? 'bg-muted/50' : undefined}>
                  <TableCell className='font-semibold text-foreground'>Custom</TableCell>
                  <TableCell>
                    <Input
                      type='number'
                      min='0'
                      aria-label='Custom electricity'
                      placeholder='Custom value'
                      value={customFactors.electricity}
                      onChange={(e) => updateCustomFactor('electricity', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type='number'
                      min='0'
                      aria-label='Custom gas'
                      placeholder='Custom value'
                      value={customFactors.gas}
                      onChange={(e) => updateCustomFactor('gas', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type='number'
                      min='0'
                      aria-label='Custom water'
                      placeholder='Custom value'
                      value={customFactors.water}
                      onChange={(e) => updateCustomFactor('water', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </section>

          <section className='space-y-4'>
            <div>
              <h3 className='text-base font-semibold text-foreground'>Choose your type of office</h3>
              <p className='text-sm text-muted-foreground pt-1'>
                Choose which office type is most appropriate for your organisation. Choose Custom to calculate with the
                figures in the Custom row.
              </p>
            </div>
            <div className='max-w-lg'>
              <Label htmlFor='office-type'>Office type</Label>
              <div className='mt-2'>
                <Select
                  id='office-type'
                  options={OFFICE_TYPE_OPTIONS}
                  value={selectedOfficeTypeId}
                  onChange={setSelectedOfficeTypeId}
                  placeholder='Select office type'
                  searchable
                />
              </div>
            </div>
            {workingDaysOverLimit && (
              <Alert variant='warning'>
                <TriangleAlert size={16} strokeWidth={1.5} />
                <AlertDescription>
                  Daily rates are not calculated while total working days in the year is over 366.
                </AlertDescription>
              </Alert>
            )}
            <div className='grid gap-4 sm:grid-cols-3'>
              <div className='rounded-lg border border-border bg-muted/30 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Office kWh Elec per m² per day
                </p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {workingDaysOverLimit ? 'Not calculated' : formatNumber(electricityPerM2PerDay)}
                </p>
              </div>
              <div className='rounded-lg border border-border bg-muted/30 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Office kWh Gas per m² per day
                </p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {workingDaysOverLimit ? 'Not calculated' : formatNumber(gasPerM2PerDay)}
                </p>
              </div>
              <div className='rounded-lg border border-border bg-muted/30 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  m³ Water per person per day
                </p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {workingDaysOverLimit ? 'Not calculated' : formatNumber(waterPerPersonPerDay)}
                </p>
              </div>
            </div>
          </section>

          <section className='space-y-4'>
            <div>
              <h3 className='text-base font-semibold text-foreground'>Calculate your totals</h3>
              <ul className='text-sm text-muted-foreground pt-2 space-y-2 list-disc pl-5'>
                <li>
                  Electricity: multiply kWh of electricity per m² per day by the m² your organisation uses, then by the
                  number of days you occupied the office in the year.
                </li>
                <li>
                  Gas: multiply kWh of gas per m² per day by the m² your organisation uses, then by the number of days
                  you occupied the office in the year.
                </li>
                <li>
                  Water: multiply water use per employee per day by the number of employees in the office, then by the
                  number of days you occupied the office in the year.
                </li>
              </ul>
            </div>
            <div className='grid gap-4 sm:grid-cols-3'>
              <div>
                <Label htmlFor='floor-area'>Floor area (m²)</Label>
                <Input
                  id='floor-area'
                  type='number'
                  min='0'
                  className='mt-2'
                  value={floorArea}
                  onChange={(e) => setFloorArea(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor='employee-count'>Employees in the office</Label>
                <Input
                  id='employee-count'
                  type='number'
                  min='0'
                  className='mt-2'
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor='days-occupied'>Days occupied</Label>
                <Input
                  id='days-occupied'
                  type='number'
                  min='0'
                  max={MAX_DAYS_IN_YEAR}
                  className='mt-2'
                  value={daysOccupied}
                  error={daysOccupiedOverLimit}
                  aria-invalid={daysOccupiedOverLimit}
                  aria-describedby={
                    daysOccupiedOverLimit ? 'days-occupied-help days-occupied-error' : 'days-occupied-help'
                  }
                  onChange={(e) => setDaysOccupied(e.target.value)}
                />
                <p id='days-occupied-help' className='text-xs text-muted-foreground mt-2'>
                  Days your organisation occupied the office in the year. Maximum 366.
                </p>
              </div>
            </div>
            {daysOccupiedOverLimit && (
              <Alert variant='warning' id='days-occupied-error'>
                <TriangleAlert size={16} strokeWidth={1.5} />
                <AlertDescription>{DAYS_OCCUPIED_LIMIT_MESSAGE}</AlertDescription>
              </Alert>
            )}
            {workingDaysOverLimit && !daysOccupiedOverLimit && (
              <Alert variant='warning'>
                <TriangleAlert size={16} strokeWidth={1.5} />
                <AlertDescription>
                  Totals are not calculated while total working days in the year is over 366.
                </AlertDescription>
              </Alert>
            )}
            <div className='grid gap-4 sm:grid-cols-3'>
              <div className='rounded-lg border border-border p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Total electricity</p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {totalsBlocked ? 'Not calculated' : `${formatNumber(electricityTotal, 2)} kWh`}
                </p>
              </div>
              <div className='rounded-lg border border-border p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Total gas</p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {totalsBlocked ? 'Not calculated' : `${formatNumber(gasTotal, 2)} kWh`}
                </p>
              </div>
              <div className='rounded-lg border border-border p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Total water</p>
                <p className='text-lg font-semibold text-foreground pt-2'>
                  {totalsBlocked ? 'Not calculated' : `${formatNumber(waterTotal, 2)} m³`}
                </p>
              </div>
            </div>
          </section>

          <p className='text-xs text-muted-foreground'>© Compare Your Footprint Limited</p>
        </CardContent>
      </Card>
    </React.Fragment>
  );
};

export default CommunalOfficeSpaces;
